import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { LayoutDashboard, CalendarDays, ListChecks, BookOpen, Settings, LogOut, Sparkles, Upload, Megaphone, Shield, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const baseNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/dashboard/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/dashboard/assignments", label: "Assignments", icon: ListChecks },
  { to: "/dashboard/courses", label: "Courses", icon: BookOpen },
  { to: "/dashboard/settings", label: "Settings", icon: Settings },
];

function NotificationsBell({ tid = "notifications-bell" }) {
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
  const onOpen = (o) => { setOpen(o); if (o && data.unread) api.post("/notifications/read").then(() => setData((d) => ({ ...d, unread: 0 }))); };
  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button className="relative text-slate-500 hover:text-slate-900" data-testid={tid}>
          <Bell className="h-5 w-5" />
          {data.unread > 0 && <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center">{data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-panel">
        <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-900">Notifications</div>
        <div className="max-h-80 overflow-auto">
          {data.notifications.length === 0 ? <p className="p-4 text-sm text-slate-400">No notifications yet.</p> :
            data.notifications.map((n) => (
              <div key={n.id} className="px-4 py-3 border-b border-slate-50">
                <p className="text-sm font-medium text-slate-900">{n.title}</p>
                <p className="text-xs text-slate-500">{n.body}</p>
              </div>
            ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isAmbassador, setIsAmbassador] = useState(false);

  useEffect(() => {
    api.get("/ambassador/application/status").then((r) => setIsAmbassador(!!r.data.ambassador_id)).catch(() => {});
  }, []);

  const nav = [...baseNav];
  if (isAmbassador) nav.push({ to: "/dashboard/ambassador", label: "Ambassador", icon: Megaphone });
  if (user?.is_admin) nav.push({ to: "/dashboard/admin", label: "Campus Ambassadors", icon: Shield });

  const isActive = (item) =>
    item.end ? location.pathname === item.to : location.pathname.startsWith(item.to);

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex w-64 flex-col border-r border-slate-200 bg-white sticky top-0 h-screen">
        <div className="px-5 py-5 border-b border-slate-100">
          <Link to="/dashboard"><Logo /></Link>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {nav.map((item) => (
            <button key={item.to} onClick={() => navigate(item.to)} data-testid={`sidebar-${item.label.toLowerCase()}`}
              className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive(item) ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              }`}>
              <item.icon className="h-5 w-5" /> {item.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-100 space-y-2">
          {user && !user.paid_status && (
            <button onClick={() => navigate("/upgrade")} data-testid="sidebar-upgrade"
              className="w-full flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-3 py-2.5 text-sm font-medium transition-colors">
              <Sparkles className="h-4 w-4" /> Unlock Syllabify
            </button>
          )}
          <div className="flex items-center gap-2 px-2 py-1.5">
            <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-600 overflow-hidden">
              {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user?.name?.[0] || "U")}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.paid_status ? "Lifetime" : "Free"}</p>
            </div>
            <button onClick={logout} data-testid="logout-btn" className="text-slate-400 hover:text-slate-700">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <div className="md:hidden sticky top-0 z-40 flex items-center justify-between border-b border-slate-200 bg-white/95 backdrop-blur px-4 py-3">
          <Link to="/dashboard"><Logo size="sm" /></Link>
          <div className="flex items-center gap-4">
            <NotificationsBell tid="notifications-bell-mobile" />
            <button onClick={logout} data-testid="logout-btn-mobile" className="text-slate-400"><LogOut className="h-5 w-5" /></button>
          </div>
        </div>

        {/* Desktop top bar */}
        <div className="hidden md:flex items-center justify-end border-b border-slate-200 bg-white px-10 py-3">
          <NotificationsBell />
        </div>

        <main className="flex-1 px-4 sm:px-6 lg:px-10 py-6 lg:py-10 pb-24 md:pb-10 max-w-6xl w-full mx-auto">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-slate-200 bg-white flex items-center justify-around px-2 py-2">
        {nav.map((item) => (
          <button key={item.to} onClick={() => navigate(item.to)} data-testid={`bottomnav-${item.label.toLowerCase()}`}
            className={`flex flex-col items-center gap-0.5 px-2 py-1 text-[10px] font-medium ${
              isActive(item) ? "text-blue-600" : "text-slate-400"
            }`}>
            <item.icon className="h-5 w-5" /> {item.label}
          </button>
        ))}
      </nav>
    </div>
  );
}
